import { type CSSProperties, type FormEvent, useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import BrandLogo from '../../components/BrandLogo'
import { publicSignatureApi, type PublicSignatureOpen } from '../../api/signatures'
import { ApiError } from '../../api/client'
import { Button, Callout, Card, CardContent, Checkbox, TextField } from '../../components/ui'

/**
 * R5-02 (Z-12) — pagina pubblica `/c/f/:token`: firma elettronica Equal. Il token si tiene solo in sessionStorage e si
 * toglie dalla barra degli indirizzi; alle API va in un header. Passi: codice via email → lettura del documento →
 * nome, luogo e accettazione → firma → copia firmata (PDF con attestazione e impronta).
 */
const PAGE_STYLE: CSSProperties = {
  minHeight: '100svh',
  paddingTop: 'var(--md-safe-top)',
  paddingBottom: 'var(--md-safe-bottom)',
}
const STORAGE_KEY = 'ecrm_signature_link'

type Step = 'loading' | 'invalid' | 'otp' | 'read' | 'done'

function readToken(fromUrl: string | undefined): string {
  if (fromUrl) {
    try { sessionStorage.setItem(STORAGE_KEY, fromUrl) } catch { /* ignore */ }
    window.history.replaceState(null, '', window.location.pathname.replace(/\/c\/f\/.*$/, '/c/f'))
    return fromUrl
  }
  try { return sessionStorage.getItem(STORAGE_KEY) ?? '' } catch { return '' }
}

function messageOf(e: unknown, fallback: string): string {
  return e instanceof Error && e.message ? e.message : fallback
}

function isTerminal(e: unknown): boolean {
  return e instanceof ApiError && (e.status === 404 || e.status === 410 || e.status === 423)
}

function saveBlob(blob: Blob, name: string) {
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url; a.download = name
  document.body.appendChild(a); a.click(); a.remove()
  setTimeout(() => URL.revokeObjectURL(url), 10_000)
}

export function SignaturePage() {
  const params = useParams<{ token?: string }>()
  const [token] = useState(() => readToken(params.token))
  const [step, setStep] = useState<Step>('loading')
  const [message, setMessage] = useState('')
  const [error, setError] = useState('')
  const [info, setInfo] = useState('')
  const [opened, setOpened] = useState<PublicSignatureOpen | null>(null)
  const [code, setCode] = useState('')
  const [session, setSession] = useState<string | null>(null)
  const [docUrl, setDocUrl] = useState<string | null>(null)
  const [docHash, setDocHash] = useState<string | null>(null)
  const [name, setName] = useState('')
  const [place, setPlace] = useState('')
  const [accepted, setAccepted] = useState(false)
  const [signedHash, setSignedHash] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  function fail(e: unknown, fallback: string) {
    if (isTerminal(e)) {
      setMessage(messageOf(e, 'Link non valido.'))
      setStep('invalid')
      try { sessionStorage.removeItem(STORAGE_KEY) } catch { /* ignore */ }
    } else if (e instanceof ApiError && e.status === 401) {
      setSession(null); setStep('otp'); setError(messageOf(e, fallback))
    } else {
      setError(messageOf(e, fallback))
    }
  }

  useEffect(() => {
    if (!token) { setMessage('Link non valido.'); setStep('invalid'); return }
    publicSignatureApi.open(token)
      .then(r => { setOpened(r); setStep(r.signed ? 'done' : 'otp') })
      .catch(e => fail(e, 'Link non valido.'))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token])

  useEffect(() => () => { if (docUrl) URL.revokeObjectURL(docUrl) }, [docUrl])

  async function sendCode() {
    setBusy(true); setError(''); setInfo('')
    try {
      await publicSignatureApi.sendOtp(token)
      setInfo(opened?.emailHint ? `Ti abbiamo inviato un codice a ${opened.emailHint}.` : 'Ti abbiamo inviato un codice via email.')
    } catch (e) { fail(e, 'Invio del codice non riuscito.') } finally { setBusy(false) }
  }

  async function verify(ev: FormEvent) {
    ev.preventDefault()
    setBusy(true); setError('')
    try {
      const r = await publicSignatureApi.verifyOtp(token, code)
      const d = await publicSignatureApi.document(token, r.session)
      setSession(r.session)
      setDocUrl(URL.createObjectURL(new Blob([d.blob], { type: 'application/pdf' }))); setDocHash(d.hash)
      setCode(''); setInfo(''); setStep('read')
    } catch (e) { fail(e, 'Codice non corretto.') } finally { setBusy(false) }
  }

  async function sign(ev: FormEvent) {
    ev.preventDefault()
    if (!session) return
    setBusy(true); setError('')
    try {
      const r = await publicSignatureApi.sign(token, session, name.trim(), accepted, place.trim())
      setSignedHash(r.signedPdfHash); setSession(null); setStep('done')
    } catch (e) { fail(e, 'Firma non riuscita.') } finally { setBusy(false) }
  }

  async function download() {
    setBusy(true); setError('')
    try { saveBlob(await publicSignatureApi.signedCopy(token), 'contratto-firmato.pdf') } catch (e) { fail(e, 'Copia firmata non disponibile.') } finally { setBusy(false) }
  }

  return (
    <div style={PAGE_STYLE} className="bg-body d-flex justify-content-center">
      <div className="w-100 p-3" style={{ maxWidth: 820 }}>
        <Card variant="outlined" padding="md">
          <CardContent>
            <div className="text-center mb-4"><BrandLogo height={56} /></div>

            {step === 'loading' && <p className="text-muted">Verifica del link…</p>}
            {step === 'invalid' && <Callout tone="error">{message}</Callout>}
            {error && step !== 'invalid' && <Callout tone="error" className="mb-3">{error}</Callout>}
            {info && <Callout tone="info" className="mb-3">{info}</Callout>}

            {step === 'otp' && (
              <form onSubmit={e => void verify(e)}>
                <h1 className="md-typescale-title-large mb-2">Firma del contratto</h1>
                {opened?.practice && <p className="text-muted md-typescale-body-small">Pratica {opened.practice}{opened.documentName ? ` — ${opened.documentName}` : ''}</p>}
                <p className="md-typescale-body-medium">
                  Per sicurezza ti mandiamo un codice di 6 cifre {opened?.emailHint ? <>all'indirizzo <strong>{opened.emailHint}</strong></> : 'via email'}.
                </p>
                <Button variant="tonal" icon="mail" loading={busy} onClick={() => void sendCode()}>Invia il codice</Button>
                <div className="mt-3">
                  <TextField
                    label="Codice"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    maxLength={6}
                    value={code}
                    onChange={e => setCode(e.currentTarget.value.replace(/\D/g, ''))}
                  />
                </div>
                <Button variant="filled" type="submit" className="mt-3" loading={busy} disabled={code.length !== 6}>Continua</Button>
              </form>
            )}

            {step === 'read' && (
              <form onSubmit={e => void sign(e)}>
                <h1 className="md-typescale-title-large mb-2">Leggi il contratto</h1>
                {docUrl && (
                  <iframe
                    title="Contratto da firmare"
                    src={docUrl}
                    style={{ width: '100%', height: '70svh', border: '1px solid var(--md-sys-color-outline-variant)', borderRadius: 8 }}
                  />
                )}
                {docUrl && (
                  <p className="md-typescale-body-small text-muted mt-1">
                    Se non vedi il documento, <a href={docUrl} download={opened?.documentName ?? 'contratto.pdf'}>scaricalo</a>.
                    {docHash && <> Impronta SHA-256: <code>{docHash}</code></>}
                  </p>
                )}
                <div className="mt-3">
                  <TextField label="Nome e cognome" autoComplete="name" maxLength={200} value={name} onChange={e => setName(e.currentTarget.value)} />
                </div>
                <div className="mt-2">
                  <TextField label="Luogo (facoltativo)" maxLength={120} value={place} onChange={e => setPlace(e.currentTarget.value)} />
                </div>
                <div className="mt-3">
                  <Checkbox
                    label="Ho letto il contratto e lo accetto. Firmo elettronicamente con il codice ricevuto via email."
                    checked={accepted}
                    onChange={e => setAccepted(e.currentTarget.checked)}
                  />
                </div>
                <Button variant="filled" icon="signature" type="submit" className="mt-3" loading={busy} disabled={!accepted || name.trim().length < 3}>
                  Firma il contratto
                </Button>
              </form>
            )}

            {step === 'done' && (
              <>
                <Callout tone="success" title="Contratto firmato">
                  Riceverai una conferma via email. Puoi scaricare ora la copia firmata, con l'attestazione e il registro delle prove.
                  {signedHash && <div className="md-typescale-body-small mt-1">Impronta SHA-256 del PDF firmato: <code>{signedHash}</code></div>}
                </Callout>
                <Button variant="tonal" icon="download" className="mt-3" loading={busy} onClick={() => void download()}>Scarica la copia firmata</Button>
              </>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  )
}
